import React from 'react'
import * as UIcons from '@iconscout/react-unicons'
import MultiLineChartCard from 'components/DetailWidgets/MultiLineChartCard'

const STAT_ICONS = {
  qualifying_transactions: UIcons.UilFileCheckAlt,
  unique_participants: UIcons.UilUsersAlt,
  campaign_sales: UIcons.UilChartLine,
  prize_cost: UIcons.UilGift,
  campaign_roi: UIcons.UilChartBar
}

const STAT_ICON_TONE = {
  success: 'bg-success-container text-on-success-container',
  information: 'bg-information-container text-primary',
  discovery: 'bg-discovery-container text-purple',
  warning: 'bg-warning-container text-gold'
}

const FUNNEL_TONE_BG = { primary: 'bg-tunnel-blue', success: 'bg-tunnel-green', gold: 'bg-tunnel-amber', danger: 'bg-tunnel-red' }

function StatCard({ icon: Icon, label, value, trend, tone }) {
  const isPositive = trend >= 0

  return (
    <div className="flex-1 min-w-[180px] h-[136px] bg-surface-container-low rounded-xl p-3 flex flex-col justify-between">
      <div className="flex items-center gap-3.5">
        <p className="flex-1 min-w-0 text-sm font-semibold text-on-surface truncate">{label}</p>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${STAT_ICON_TONE[tone] || STAT_ICON_TONE.success}`}>
          <Icon size={16} />
        </div>
      </div>

      <p className="text-xl font-semibold text-on-surface truncate">{value}</p>

      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-1 py-0.5 rounded text-xs shrink-0 ${isPositive ? 'bg-success-container text-on-success-container' : 'bg-error-container text-danger'}`}>
          {isPositive ? <UIcons.UilArrowUp size={12} /> : <UIcons.UilArrowDown size={12} />}
          {Math.abs(trend)}%
        </span>
        <span className="text-xs text-on-surface truncate">Vs last 30 days</span>
      </div>
    </div>
  )
}

function SectionCard({ title, subtitle, action, children, className = '' }) {
  return (
    <div className={`w-full h-full bg-white rounded-xl border border-outline-variant shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col ${className}`}>
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-outline-variant">
        <div>
          <p className="text-xl font-semibold text-on-surface uppercase">{title}</p>
          {subtitle && <p className="text-sm text-on-surface-variant mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="px-6 flex-1">{children}</div>
    </div>
  )
}

// Campaign Setup uses a plainer card treatment in the design — no header divider,
// no row dividers, tighter padding, 14px rows — distinct from the other section cards.
function CampaignSetupCard({ title, items }) {
  const TONE_TEXT = { success: 'text-success', danger: 'text-danger' }
  return (
    <div className="w-full h-full bg-white rounded-2xl border border-outline-variant px-4 py-2">
      <p className="text-xl font-semibold text-on-surface uppercase py-3">{title}</p>
      {
        items.map((item) => (
          <div key={item.key} className="flex items-center py-2">
            <p className="w-[190px] shrink-0 text-sm text-on-surface-variant">{item.label}</p>
            <p className={`flex-1 text-sm ${item.tone ? TONE_TEXT[item.tone] : 'text-on-surface'}`}>{item.value}</p>
          </div>
        ))
      }
    </div>
  )
}

function DashedRow({ label, value, tone, dimLabel = true }) {
  const TONE_TEXT = { success: 'text-success', danger: 'text-danger' }
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-dashed border-outline-variant last:border-0">
      <p className={dimLabel ? 'text-base text-on-surface-variant' : 'text-base text-on-surface'}>{label}</p>
      <p className={`text-base text-right ${tone ? TONE_TEXT[tone] : 'text-on-surface'}`}>{value}</p>
    </div>
  )
}

function LevelsStreaksTable({ rows }) {
  return (
    <div>
      <div className="flex items-center gap-4 py-3 border-b border-dashed border-outline-variant">
        <p className="flex-1 text-base font-semibold text-on-surface-variant">LEVELS</p>
        <p className="w-[110px] shrink-0 text-base font-semibold text-on-surface-variant">COIN BONUS</p>
        <p className="w-[150px] shrink-0 text-base font-semibold text-on-surface-variant">POINTS BONUS</p>
      </div>
      {
        rows.map((row) => (
          <div key={row.level} className="flex items-center gap-4 py-3 border-b border-dashed border-outline-variant last:border-0">
            <p className="flex-1 text-base text-on-surface uppercase">{row.level}</p>
            <p className="w-[110px] shrink-0 text-base text-on-surface">{row.coinBonus}</p>
            <p className="w-[150px] shrink-0 text-base text-on-surface">{row.pointsBonus}</p>
          </div>
        ))
      }
    </div>
  )
}

// A bespoke funnel (rather than the shared ConversionFunnel widget) so the legend can
// carry dashed dividers and color dots to match this screen's design exactly.
function bandClipPath(topWidth, bottomWidth) {
  const topInset = (100 - topWidth) / 2
  const bottomInset = (100 - bottomWidth) / 2
  return `polygon(${topInset}% 0%, ${100 - topInset}% 0%, ${100 - bottomInset}% 100%, ${bottomInset}% 100%)`
}

// The design's cone is a fixed decorative shape (one smooth taper cut into equal
// bands by thin gaps) — its width isn't driven by each step's data percentage.
const CONE_TOP_WIDTH = 100
const CONE_BOTTOM_WIDTH = 20
function coneTaperWidth(index, total) {
  return CONE_TOP_WIDTH + (CONE_BOTTOM_WIDTH - CONE_TOP_WIDTH) * (index / total)
}

function ConversionTunnel({ steps }) {
  return (
    <div className="flex items-center gap-6 py-6">
      <div className="flex-1 max-w-[220px] flex flex-col gap-1">
        {
          steps.map((step, index) => {
            const topWidth = coneTaperWidth(index, steps.length)
            const bottomWidth = coneTaperWidth(index + 1, steps.length)
            return (
              <div
                key={step.key}
                className={`w-full h-12 ${FUNNEL_TONE_BG[step.tone] || 'bg-primary'}`}
                style={{ clipPath: bandClipPath(topWidth, bottomWidth) }}
              />
            )
          })
        }
      </div>

      <div className="flex-1 border border-outline-variant rounded-lg px-4">
        {
          steps.map((step, index) => (
            <div key={step.key} className={`flex items-center gap-3 py-3 ${index < steps.length - 1 ? 'border-b border-dashed border-outline-variant' : ''}`}>
              <span className={`w-3 h-3 rounded-full shrink-0 ${FUNNEL_TONE_BG[step.tone] || 'bg-primary'}`} />
              <p className="flex-1 min-w-0 text-sm text-on-surface truncate">{step.label}</p>
              <p className="text-sm text-on-surface text-right whitespace-nowrap">{step.value.toLocaleString()}</p>
              <p className="w-10 shrink-0 text-sm text-on-surface-variant text-right">{step.percent}%</p>
            </div>
          ))
        }
      </div>
    </div>
  )
}

export default function OverviewTab({ campaign }) {
  const { overview } = campaign

  return (
    <div className="flex flex-col gap-6">
      <p className="text-[28px] leading-9 font-semibold text-on-surface">Overview</p>

      <div className="flex flex-wrap items-stretch gap-4">
        {
          overview.stats.map((stat) => (
            <StatCard
              key={stat.key}
              icon={STAT_ICONS[stat.key] || UIcons.UilChartLine}
              label={stat.label}
              value={stat.value}
              trend={stat.trend}
              tone={stat.tone}
            />
          ))
        }
      </div>

      <MultiLineChartCard
        title="Campaign Overview"
        subtitle="Sales Over Time (NGN)"
        series={overview.salesOverTime.series}
        valueFormatter={(value) => `NGN ${Math.round(value / 1000)}K`}
        className="w-full bg-white rounded-xl border border-outline-variant shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] p-6"
      />

      <div className="grid md:grid-cols-2 gap-4 items-stretch">
        <CampaignSetupCard title="Campaign Setup" items={overview.setup} />

        <SectionCard title="Conversion Tunnel">
          <ConversionTunnel steps={overview.funnel} />
        </SectionCard>
      </div>

      <div className="grid md:grid-cols-2 gap-4 items-stretch">
        <SectionCard
          title="Top Prizes"
          action={<button type="button" className="text-base font-semibold text-primary flex items-center gap-1 whitespace-nowrap">View all prizes <UIcons.UilArrowRight size={20} /></button>}
        >
          {overview.topPrizes.map((prize) => <DashedRow key={prize.key} label={prize.name} value={prize.value} dimLabel={false} />)}
        </SectionCard>

        <SectionCard title="Levels & Streaks">
          <LevelsStreaksTable rows={overview.levelsStreaks} />
        </SectionCard>
      </div>
    </div>
  )
}
